# Solresor single hotel
<template>
  <div class="container mx-auto mt-4 lg:mt-12 pb-12 pl-0 px-4">
    <div class="flex flex-wrap">
      <div class="w-full">
        <div class="flex align-items-baseline">
          <h1 class="capitalize mb-4 font-secondary">
            {{ hotelInfo.propertyName }}
          </h1>
          <div class="ml-2">
            <div class="pb-0 product__testimonials__stars">
              <div
                v-for="i in parseInt(hotelInfo.rating || 0)"
                :key="`star-${i}`"
                class="review-star c-orange"
              >
                <StarByLocale :size="16" />
              </div>
            </div>
          </div>
        </div>
        <nuxt-link
          class="font-bold mt-1"
          :to="destinationLink"
        >
          <fa icon="fa-location-dot" />
          <span class="ml-2">
            {{ displayDestination }}
          </span>
        </nuxt-link>
        <div class="flex gap-2 flex-wrap my-4">
          <LazyCampaignTag
            v-for="tag in filteredUniqueTags"
            :key="`tag-b-${tag.id}`"
            class="mb-2 rounded-2xl"
            :tag="tag"
            :show-icon="true"
            size="md"
          />
        </div>
        <div class="relative">
          <LazyHotelPreviewSwiperGallery
            :hotel="hotelInfo"
            @open-gallery="openGallery"
          />
        </div>
        <div class="absolute">
          <LazyRolintLink
            class="m-1"
            :giata-id="hotelInfo.giataId"
          />
        </div>
        <div class="hotel-info-container flex mt-4 flex-col lg:flex-row">
          <div class="my-4 whitespace-pre-wrap">
            {{ hotelInfo.description }}
          </div>
          <ReviewRatings :hotel-info="hotelInfo" />
        </div>
        <div
          v-for="(facility, index) in filteredFacilities"
          :key="`f${index}`"
          class="inline-block mr-6"
        >
          <fa :icon="getIcon(facility)" />
          <span class="ml-2 font-bold">
            {{ $t(`hotelFacility.${facility.trim()}`) }}
          </span>
        </div>

        <div class="mt-16 mb-6">
          <LazyPackageSearchBar
            v-if="dfDisabled"
            class="mt-4 lg:mt-5"
            :max-calendars="2"
            @search="doSearch"
          />
          <LazyDealFinder
            v-else
            emit-selection
            @select="onSelectDay"
          />
        </div>

        <HotelMapBoxViewer
          class="mb-8"
          :hotel-info="hotelInfo"
        />
      </div>

      <LazyDestinationWeather
        :content="weather"
        :hide-text="true"
      />
      <LazyCharterHotelFacilities
        :hotel-info="hotelInfo"
        title="hotelFacilitiesTitle"
      />
      <LazyDestinationHotels
        v-if="destinationId"
        :destination-id="destinationId"
        :is-hotel-page="true"
        title="charterHotelActivitiesTitle"
      />
    </div>
    <FullscreenImageCarousel
      v-if="showGallery"
      :key="hgKey"
      :images="roomImages || images(hotelInfo)"
      :visible="showGallery"
      @on-hide="showGallery = false"
    />
    <LazyHotelModal
      :hotels="[selectedTrip]"
      :selected-rooms="selectedRooms"
      :request-state="true"
      :error-booking="!!error"
      book-type="charterpackages"
      dealfinder
      @select-room="selectRoom($event.index, $event.room)"
      @un-select-rooms="unSelectRooms"
      @go-next="goNext"
      @select-hotel="selectHotel"
      @close="resetErrors"
    >
      <template #roomPrice="{ totalRoomPrices }">
        <div class="mr-6 inline-block">
          <small class="font-bold mb-1">
            {{ $t('bookTotalPriceWithPassengers', numberOfPassengers) }}
          </small>
          <strong class="ml-2 text-nowrap h5 font-bold">
            {{ $n(totalRoomPrices) }}
          </strong>
        </div>
      </template>
    </LazyHotelModal>
  </div>
</template>

<script setup>
defineEmits(['bv::modal::hide'])

const scrollToDealfinder = () => {
  // DEALFINDER
  const df = document.querySelector('.charter-destination__deal-finder')

  if (df) {
    scrollTo(df)
  }
}

defineExpose({ scrollToDealfinder })

const props = defineProps({
  hotelInfo: {
    type: Object,
    required: true,
  },
})

const { $event: event, $sentryCaptureMessage: sentryCaptureMessage } = useNuxtApp()
const { urls: localeURLs } = useLocale()
const { scrollTo } = useScrollMixin()
const { featuredImg, getIcon, images } = useCharterHotelMixin()
const { imageSize } = useHotelImagesMixin()
const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const charterPackagesStore = useCharterPackagesStore()
const charterHotelStore = useCharterHotelStore()

const bookStore = useBookStore()

const showModal = ref(false)
const { selectedRooms, rooms } = storeToRefs(charterPackagesStore)

const weather = charterHotelStore.GET_CHARTER_HOTEL_WEATHER

const ogImage = featuredImg(props.hotelInfo)

useHead({
  title: () => `${props.hotelInfo.propertyName}, ${props.hotelInfo.townName}`,
  link: [
    {
      rel: 'canonical',
      href: () => `${localeURLs.BASE_URL}${localeURLs.hotel}/${props.hotelInfo.url_name}`,
    },
  ],

  meta: [
    { hid: 'author', name: 'author', content: () => t('siteTitleBrand') },
    {
      hid: 'og:url',
      property: 'og:url',
      content: () => `${localeURLs.BASE_URL}${localeURLs.hotel}/${props.hotelInfo.url_name}`,
    },
    {
      hid: 'og:title',
      property: 'og:title',
      content: () => `${props.hotelInfo.propertyName}, ${props.hotelInfo.townName} - ${t('siteTitleBrand')}`,
    },
    ...(!ogImage ? [] : [{
      hid: 'og:image',
      property: 'og:image',
      content: () => imageSize(ogImage, 'og', true),
    }]),
    {
      hid: 'og:description',
      name: 'og:description',
      content: () => trimByWord(props.hotelInfo.description),
    },
    {
      hid: 'description',
      name: 'description',
      content: () => trimByWord(props.hotelInfo.description),
    },
  ],
})

const error = ref(false)

const dfDisabled = route.query?.df === 'false'
const selectedTrip = ref({})

const roomImages = ref(null)

const showGallery = ref(false)
const hgKey = ref(1)
const errorBooking = ref(false)

const filteredUniqueTags = computed(() => {
  const filteredArray = (props.hotelInfo.charterTags || [])
    .filter(tag => !isEmpty(tag?.position))
    .filter(charterTagIsNotCampaign)

  return uniqueArray(filteredArray, 'id')
})

const filteredFacilities = computed(() => {
  return props.hotelInfo?.highlighted_facilities?.filter(facility => facility)
})

const destinationLink = computed(() => {
  const destination = props.hotelInfo.destination?.destination_page_id ||
    props.hotelInfo.displayDestination?.destination ||
    props.hotelInfo.displayDestination?.zone || ''

  return `${localeURLs.destinations}/${destination.toLowerCase()}`
})

const destinationId = computed(() => {
  return props.hotelInfo.destination_id || props.hotelInfo.destination?.destination_id
})

const displayDestination = computed(() => {
  let parts = [...new Set([
    props.hotelInfo.displayDestination?.zone,
    props.hotelInfo.displayDestination?.destination,
    props.hotelInfo.displayDestination?.country,
  ])].filter(Boolean)

  if (parts.length === 0) {
    parts = [
      props.hotelInfo.townName, props.hotelInfo.country,
    ].filter(Boolean)
  }
  return parts.join(', ')
})

const numberOfPassengers = computed(() => {
  return rooms.value.reduce((acc, cur) => acc + cur.Adults + cur.Children, 0)
})

onMounted(async () => {
  if (route.query.showRooms) {
    const query = { ...route.query }
    delete query.showRooms

    try {
      return await router.replace({ query })
    } catch {
      // do nothing
    }
  }
})

const resetErrors = () => {
  error.value = false
}

const openGallery = (images) => {
  roomImages.value = null
  if (images && Array.isArray(images)) {
    roomImages.value = images
  }

  showGallery.value = true

  nextTick(() => {
    hgKey.value++
  })
}

const doSearch = async (params) => {
  params.hotel = props.hotelInfo.id

  const options = await charterPackagesStore.getURLParameters(params)

  navigateTo(`${localeURLs.packagessearch}?${options}`)
}

const onSelectDay = (day) => {
  selectedTrip.value = {
    ...props.hotelInfo,
    hotel_code: day.hotel_code,
    destination: day.destination,
    date: day.date,
    return_date: day.return,
    origin: day.origin,
    price: day.price,
    total_price: day.total_price,
    supplier: day.supplier,
  }
  selectHotel(selectedTrip.value)
}

const selectRoom = (index, room) => {
  const rooms = deepClone(selectedRooms.value)
  rooms[index] = room
  charterPackagesStore.SET_SELECTED_ROOMS(rooms)
}

const selectHotel = (hotel) => {
  if (!route.query.showRooms) {
    const id = typeof hotel === 'object' ? hotel.id : hotel
    navigateTo({ query: { ...route.query, showRooms: id } })
  }
}

const unSelectRooms = () => {
  errorBooking.value = false
  charterPackagesStore.SET_SELECTED_ROOMS([])
  charterPackagesStore.SET_MODALROOMTYPES([])
  bookStore.SET_ROOMGROUP({})
}

const goNext = async () => {
  // DEALFINDER
  try {
    const params = {
      selected_rooms: transformSelectedRoomsToRequestReady(selectedRooms.value),
    }

    await charterPackagesStore.customerData(params)
    bookStore.SET_STEP(2)

    // hide event for room modal
    event.$emit('bv::modal::hide')
    return navigateTo(`${localeURLs.book}/charter/3`)
  } catch (e) {
    if (e.data?.error) {
      if (e.data.message === 'state_expired') {
        return
      }

      sentryCaptureMessage('charter/customerData error', {
        level: 'info',
        contexts: {
          details: e.data,
        },
        tags: {
          type: 'UX',
          source: 'booking',
        },
      })

      error.value = e.data?.message || t('serverError')
      showModal.value = true
      return
    }

    error.value = t('serverError')
    showModal.value = true
  }
}
</script>

<style lang="scss">
@import "@layers/web/assets/scss/modules/_hotel-gallery";
@import "@layers/web/assets/scss/modules/product/_tripadvisor";
@import "@layers/web/assets/scss/modules/product/_product-testimonials";

@import "@layers/web/assets/scss/modules/charter/_charter-destination";
@import "@layers/web/assets/scss/modules/charter/_charter-results";

.gallery-modal {
  overflow: hidden;

  .modal-content {
    background: transparent;
    border: none;

    height: 100%;
  }

  .modal-backdrop {
    opacity: 0.8;
  }

  .modal-body {
    padding: 0;
  }
}

.hotel-info-container {
  gap: 0;
  @media(min-width: theme('screens.lg')) {
    gap: 2rem;
  }
}
</style>
